import { Component, OnInit, OnDestroy } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Store } from '@ngrx/store';
import { Subject, takeUntil } from 'rxjs';
import { QuickBooksService } from 'src/app/services/quickbooks.service';
import { AppSelectionService } from 'src/app/services/app-selection.service';
import { AppState } from 'src/app/store/app-state.reducer';
import { setSelectedApplication } from 'src/app/store/app-state.actions';

/** Where we land if we can't tell where the flow started. */
const FALLBACK_RETURN_URL = '/dashboard/admin/integrations-config';

/**
 * Where Intuit sends the browser after the user approves access.
 *
 * A transit route, not a page: it exchanges what came off the URL and forwards,
 * replacing the URL so it never becomes a Back target. It exists as its own
 * route rather than pointing Intuit at the integrations screen because this
 * arrives as a cold load carrying a single-use code — and every guard between
 * here and a deep admin URL is a chance to redirect and lose it. This route
 * carries MsalGuard and nothing else.
 */
@Component({
  selector: 'bhub-quickbooks-callback',
  templateUrl: './quickbooks-callback.component.html',
  styleUrls: ['./quickbooks-callback.component.scss'],
  standalone: false
})
export class QuickBooksCallbackComponent implements OnInit, OnDestroy {
  error: string | null = null;

  private returnUrl = FALLBACK_RETURN_URL;
  private destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private store: Store<AppState>,
    private quickBooksService: QuickBooksService,
    private appSelection: AppSelectionService,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    // Read before scrubbing — the snapshot is the only place these exist.
    const params = this.route.snapshot.queryParamMap;
    const code = params.get('code');
    const realmId = params.get('realmId');
    const state = params.get('state');

    // Take the credential out of the address bar immediately. This doesn't undo
    // the browser history entry, but it stops the values surviving into
    // screenshots, copied links, and later navigation.
    this.location.replaceState('/quickbooks/callback');

    const pending = this.quickBooksService.readPendingConnect();
    if (pending) {
      this.returnUrl = pending.returnUrl || FALLBACK_RETURN_URL;
    }

    if (!code || !realmId || !state) {
      this.fail('This connection attempt could not be completed. Please try connecting again.');
      return;
    }

    // The exchange must carry the application the flow started under. Nothing
    // has resolved one on a cold load, so re-select it here: the tab-scoped
    // stash first, falling back to the persisted choice. Getting this wrong is
    // a 400 that leaves the state unconsumed, so a retry still works.
    const applicationId = pending?.applicationId ?? this.appSelection.getPersistedAppId();
    if (applicationId === null) {
      this.fail('We could not tell which application this connection belongs to. Please try connecting again.');
      return;
    }
    this.store.dispatch(setSelectedApplication({ applicationId }));

    this.quickBooksService.completeAuthorization({ code, realmId, state })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: connection => {
          this.quickBooksService.clearPendingConnect();
          this.snackBar.open(
            connection.companyName
              ? `Connected to ${connection.companyName}`
              : 'QuickBooks connected',
            'Close',
            { duration: 4000 }
          );
          this.router.navigateByUrl(this.returnUrl, { replaceUrl: true });
        },
        error: (error: HttpErrorResponse) => {
          console.error('QuickBooks authorization failed:', error);
          this.fail(this.describeError(error));
        }
      });
  }

  /** Leaves the user on this screen with an explanation and a way back. */
  private fail(message: string): void {
    this.quickBooksService.clearPendingConnect();
    this.error = message;
  }

  private describeError(error: HttpErrorResponse): string {
    switch (error.status) {
      case 400:
        // Unknown, expired, replayed, or from another application — the server
        // deliberately doesn't say which, so one message covers all four.
        return 'This connection attempt could not be completed. Please try connecting again.';
      case 403:
        return 'You need to be an owner or admin to connect QuickBooks.';
      case 409:
        return 'This QuickBooks connection has expired. Please try connecting again.';
      case 502:
        return 'QuickBooks could not be reached. Please try again.';
      case 503:
        return 'QuickBooks is not configured for this environment.';
      default:
        return 'Something went wrong connecting to QuickBooks.';
    }
  }

  goBack(): void {
    this.router.navigateByUrl(this.returnUrl, { replaceUrl: true });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
